import React, { useState, useRef } from 'react';
import { Share } from '@capacitor/share';
import './QuotesCard.css';

const QuotesCard = ({ quotes = [], isActive }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showShare, setShowShare] = useState(true);
    const touchStartX = useRef(0);
    const touchCurrentX = useRef(0);
    const isSwiping = useRef(false);
    if (!quotes || quotes.length === 0) return null;

    // Safety check for bounds
    const safeIndex = currentIndex % quotes.length;
    const currentQuote = quotes[safeIndex] || quotes[0];

    // Unified Image URL Resolver
    const quoteImgSrc = currentQuote.quoteImage || currentQuote.imageUrl || currentQuote.backgroundImage || '';

    // Direct Image layout detection
    const isDirectImage = currentQuote.type === 'direct_image' || 
        (quoteImgSrc.length > 0 && (!currentQuote.text || currentQuote.text.trim().length === 0));

    const handleTouchStart = (e) => {
        e.stopPropagation(); 
        touchStartX.current = e.touches[0].clientX;
        touchCurrentX.current = e.touches[0].clientX;
        isSwiping.current = false;
    };

    const handleTouchMove = (e) => {
        e.stopPropagation();
        touchCurrentX.current = e.touches[0].clientX;
        // If movement is greater than 10px, treat it as a swipe gesture rather than a tap
        if (Math.abs(touchStartX.current - touchCurrentX.current) > 10) {
            isSwiping.current = true;
        }
    };

    const handleTouchEnd = (e) => {
        e.stopPropagation();
        const deltaX = touchStartX.current - touchCurrentX.current;
        
        if (deltaX > 60) {
            setCurrentIndex((prev) => (prev + 1) % quotes.length);
        } else if (deltaX < -60) {
            setCurrentIndex((prev) => (prev - 1 + quotes.length) % quotes.length);
        }
    };
    const handleCardClick = () => {
        // Only toggle visibility if the user tapped, not when they finished swiping
        if (isSwiping.current) {
            isSwiping.current = false;
            return;
        }
        setShowShare((prev) => !prev);
    };

    const handleShare = async (e, quote) => {
        e.stopPropagation();
        const hasCustomText = quote.text && quote.text.trim().length > 0;
        
        const shareText = hasCustomText 
            ? `✨ ${quote.text}\n\nచదవండి మరిన్ని ఆసక్తికరమైన అంశాలు మన Ai News యాప్ లో!`
            : `చదవండి మరిన్ని ఆసక్తికరమైన అంశాలు మన Ai News యాప్ లో!`;

        const shareData = {
            title: 'Daily Motivation',
            text: shareText,
            url: `https://ainewsapp.net/quote/${quote.id}`,
        };
        try {
            await Share.share(shareData);
        } catch (error) {
            if (navigator.share) {
                navigator.share(shareData).catch(err => console.log(err));
            }
        }
    };

    return (
        <div 
            className={`quotes-card-container ${isDirectImage ? 'direct-img-layout' : 'text-bg-layout'}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={handleCardClick}
            style={{ 
                backgroundImage: isDirectImage 
                    ? 'none' 
                    : `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.75)), url("${currentQuote.backgroundImage}")`,
                backgroundColor: '#0a0d14',
                position: 'relative',
                cursor: 'pointer'
            }}
        >
            {/* Header Overlay */}
            {/* REPLACE the <div className="quotes-header"> block with this */}
<div className="quotes-header">
    <div className="quotes-header-left">
        <img src="/images/logo.png" alt="Logo" className="quotes-logo" />
        <span className="quotes-brand-text">Ai News</span>
    </div>

    {/* Top Right Circular Share Button */}
    <button 
        className={`quotes-top-share-btn ${!showShare ? 'hidden-share' : ''}`}
        onClick={(e) => handleShare(e, currentQuote)}
        aria-label="Share Quote"
    >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="share-svg-icon">
            <circle cx="18" cy="5" r="3"></circle>
            <circle cx="6" cy="12" r="3"></circle>
            <circle cx="18" cy="19" r="3"></circle>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
    </button>
</div>

            {/* Direct Image Render Engine (With inline layout configurations to override WebView caching) */}
            {isDirectImage ? (
                <>
                    <div 
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#0a0d14',
                            zIndex: 2
                        }}
                    >
                        <img 
                            key={`${currentQuote.id || safeIndex}-${quoteImgSrc}`} // 💡 Unique key forces image reload on swipe
                            src={quoteImgSrc} 
                            alt="Daily Motivation card" 
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                            crossOrigin="anonymous"
                        />
                    </div>
                    {/* Invisible spacer to maintain vertical layout flow and keep dots at the bottom */}
                    <div className="quotes-middle-content" style={{ opacity: 0, pointerEvents: 'none' }} />
                </>
            ) : (
                <div className="quotes-middle-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
                    <p className="quote-body">"{currentQuote.text}"</p>
                    {currentQuote.author && (
                        <span 
                            className="quote-author" 
                            style={{ 
                                color: '#ff9800', 
                                fontSize: '13.5px', 
                                fontWeight: '700', 
                                textShadow: '0 2px 4px rgba(0,0,0,0.8)' 
                            }}
                        >
                            - {currentQuote.author}
                        </span>
                    )}
                </div>
            )}

            {/* Slide Navigation Indicator (Dots) - only show if there is more than 1 card */}
            {quotes.length > 1 && (
                <div className="quotes-carousel-indicators" style={{ zIndex: 10 }}>
                    {quotes.map((_, idx) => (
                        <span 
                            key={idx} 
                            className={`quotes-dot ${idx === currentIndex ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                        />
                    ))}
                </div>
            )}

            {/* Bottom Sharing Action
            <div className="quotes-actions-bar" style={{ 
                zIndex: 10,
                opacity: showShare ? 1 : 0,                 // Fade effect
                    pointerEvents: showShare ? 'auto' : 'none',  // Prevent clicking button while invisible
                    transition: 'opacity 0.25s ease-in-out'      // Smooth fade transition
             }}>
                <button className="quotes-whatsapp-share" onClick={(e) => handleShare(e, currentQuote)}>
                    <svg className="whatsapp-svg" viewBox="0 0 448 512" fill="currentColor">
                        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
                    </svg>
                    SHARE
                </button>
            </div> */}
        </div>
    );
};

export default QuotesCard;